import { useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import styled from 'styled-components';
import { useProjectWorkspaceIdFragment } from '@frameio/graphql-client/generated';
import { getProjectIdFromPath } from '@frameio/shared/routing/pages/project';
import { theme, useHoverIntent } from '@frameio/vapor';
import { useGetProjectsForWorkspaceNav } from '../../../../hooks';
import { ProjectList } from '../project-list/ProjectList';
import TEXT from './generated/TEXT/WorkspaceItem';
import {
  WorkspaceName,
  WorkspaceRow,
  WorkspaceRowContainer,
} from './WorkspaceRow';

type WorkspaceItemProps = {
  workspaceId: string;
  contextMenuDisabled?: boolean;
};

export const WorkspaceItem = ({
  workspaceId,
  contextMenuDisabled = false,
}: WorkspaceItemProps) => {
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  const { bind } = useHoverIntent(
    () => {
      if (!shouldPrefetch) {
        setShouldPrefetch(true);
      }
    },
    {
      velocity: 0.2,
    }
  );

  const { projectIds, loading, fetchMore, hasNextPage, totalCount } =
    useGetProjectsForWorkspaceNav({
      workspaceId,
      pageSize: 5,
      skip: !shouldPrefetch,
    });

  const path = usePathname();
  const currentProjectId = getProjectIdFromPath(path);
  const { data: workspaceData } =
    useProjectWorkspaceIdFragment(currentProjectId);
  const projectWorkspaceId = workspaceData?.workspaceId ?? null;

  const projects = useMemo<typeof projectIds>(() => {
    if (!currentProjectId || projectWorkspaceId !== workspaceId) {
      return projectIds;
    }
    return [
      currentProjectId,
      ...projectIds.filter((id) => id !== currentProjectId),
    ];
  }, [currentProjectId, projectIds, workspaceId, projectWorkspaceId]);

  return (
    <WorkspaceRow
      contextMenuDisabled={contextMenuDisabled}
      shouldExpand={!loading && Array.isArray(projectIds)}
      workspaceId={workspaceId}
      {...bind}
    >
      <ProjectList
        fetchMore={fetchMore}
        loading={loading}
        moreButtonEnabled={
          (hasNextPage ?? false) && (totalCount ?? 0) > 0 && !loading
        }
        projectIds={projects}
        showMoreLabel={
          totalCount
            ? TEXT.showMore(totalCount - projectIds.length)?.toString()
            : undefined
        }
      />
    </WorkspaceRow>
  );
};

export const WorkspaceItemContainer = styled.div`
  font-size: ${theme.fontSizes[70]};
  padding: 0 ${theme.spaces[12]};
  width: 100%;

  &[aria-selected='true'] ${WorkspaceName} {
    color: ${theme.colors.text.primary};
  }

  &[aria-selected='true'] ${WorkspaceRowContainer} {
    background: ${theme.colors.background.interactive.activated};
  }
`;

TEXT.DEFINE({
  /** Aria label for the workspace options menu button */
  ariaLabel: 'More options',
  /** Button text to show more projects */
  showMore: (count: number) => `+${count} More`,
});
